/**
 * Cookie Consent Manager
 * Handles GDPR/ePrivacy/CCPA compliant cookie consent storage and management
 */

export interface ConsentPreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

const CONSENT_COOKIE_NAME = 'cc_consent';
const CONSENT_EXPIRY_DAYS = 180; // 6 months

/**
 * Get current consent preferences from cookie
 */
export const getConsentPreferences = (): ConsentPreferences | null => {
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${CONSENT_COOKIE_NAME}=`));

  if (!cookie) return null;

  try {
    const value = cookie.split('=')[1];
    const decoded = decodeURIComponent(value);
    const prefs = JSON.parse(decoded) as ConsentPreferences;

    // Check if consent has expired (6 months)
    const now = Date.now();
    const expiryMs = CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    if (now - prefs.timestamp > expiryMs) {
      // Consent expired, clear it
      clearConsent();
      return null;
    }

    return prefs;
  } catch (e) {
    return null;
  }
};

/**
 * Save consent preferences to cookie
 */
export const saveConsentPreferences = (preferences: Omit<ConsentPreferences, 'timestamp'>): void => {
  const prefs: ConsentPreferences = {
    ...preferences,
    timestamp: Date.now(),
  };

  const value = encodeURIComponent(JSON.stringify(prefs));
  const maxAge = CONSENT_EXPIRY_DAYS * 24 * 60 * 60; // seconds

  document.cookie = `${CONSENT_COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;

  // Update gtag consent mode if available
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: preferences.analytics ? 'granted' : 'denied',
      ad_storage: preferences.marketing ? 'granted' : 'denied',
    });
  }
};

/**
 * Clear all consent data
 */
export const clearConsent = (): void => {
  // Remove consent cookie
  document.cookie = `${CONSENT_COOKIE_NAME}=; path=/; max-age=0`;

  // Remove legacy localStorage (migration cleanup)
  localStorage.removeItem('analytics_consent');

  // Clear Google Analytics cookies
  clearAnalyticsCookies();

  // Update gtag to deny
  if (window.gtag) {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
    });
  }
};

/**
 * Clear all Google Analytics cookies
 */
export const clearAnalyticsCookies = (): void => {
  const gaCookies = ['_ga', '_gid', '_gat', '_gat_gtag_G_LZ4KHYDSBZ'];

  gaCookies.forEach(cookieName => {
    // Clear for current domain
    document.cookie = `${cookieName}=; path=/; max-age=0`;
    // Clear for domain with dot prefix
    document.cookie = `${cookieName}=; path=/; domain=.thrivebot.in; max-age=0`;
  });

  // Clear _ga_* cookies (dynamic suffixes)
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('_ga_')) {
      document.cookie = `${name}=; path=/; max-age=0`;
      document.cookie = `${name}=; path=/; domain=.thrivebot.in; max-age=0`;
    }
  });
};

/**
 * Check if user has given consent (not expired, and not null)
 */
export const hasConsented = (): boolean => {
  return getConsentPreferences() !== null;
};

/**
 * Accept all cookies (convenience function)
 */
export const acceptAllCookies = (): void => {
  saveConsentPreferences({
    essential: true,
    analytics: true,
    marketing: false, // Set to true if you use marketing cookies
  });
};

/**
 * Reject all non-essential cookies
 */
export const rejectAllCookies = (): void => {
  saveConsentPreferences({
    essential: true,
    analytics: false,
    marketing: false,
  });

  // Actively clear any existing tracking cookies
  clearAnalyticsCookies();
};

/**
 * Migrate legacy localStorage consent to new cookie system
 */
export const migrateLegacyConsent = (): void => {
  const legacy = localStorage.getItem('analytics_consent');

  if (legacy && !getConsentPreferences()) {
    if (legacy === 'granted') {
      acceptAllCookies();
    } else if (legacy === 'denied') {
      rejectAllCookies();
    }

    // Clean up legacy storage
    localStorage.removeItem('analytics_consent');
  }
};
